import {
  checkboxFilter,
  Filter as FilterType,
  HeaderAction,
  multiFilter,
  PageHeaderLayout,
  SuperDrawerWidth,
  useGridTableLayoutState,
  useSuperDrawer,
} from "@homebound/beam";
import { useEffect, useMemo, useState } from "react";
import {
  createBusinessFunctionsUrl,
  createChangeTypesUrl,
  createDevelopmentChangeLogDetailsUrl,
  createDevelopmentChangeLogUrl,
  createGlobalChangeLogDetailsUrl,
  createGlobalChangeLogUrl,
  createProjectChangeLogDetailsUrl,
  createProjectChangeLogUrl,
} from "src/RouteUrls";
import {
  useChangeLogMetadataQuery,
  useChangeLogProjectFilterMetadataQuery,
} from "src/components/change-log/ChangeLog.generated";
import { ChangeRequestsFilter, ChangeType, InputMaybe, Maybe } from "src/generated/graphqlTypes";
import { ChangeLogTable } from "./ChangeLogTable";
import { CreateChangeRequestDrawer } from "./CreateChangeRequestDrawer";
import { filterChangeRequestSources } from "./components/DetailsTab";

export enum ChangeLogView {
  Global,
  Development,
  Project,
}

enum ChangeLogFilter {
  MyRequests,
  Status,
  ChangeType,
  Source,
  Department,
  Market,
  Development,
  Project,
  Group,
}

export type ChangeLogTableFilter = ChangeRequestsFilter & {
  // Custom and yet to be created filters
  myRequests?: InputMaybe<boolean>;
  changeTypes?: InputMaybe<ChangeType[]>;
};

type ChangeLogProps = { view: ChangeLogView; projectId?: string; developmentId?: string };

export function ChangeLog(props: ChangeLogProps) {
  const { view, projectId, developmentId } = props;
  const { openInDrawer } = useSuperDrawer();
  const { data } = useChangeLogMetadataQuery();
  const { markets = [], developments = [], changeRequestGroups = [], enumDetails } = data ?? {};

  const [projectSearch, setProjectSearch] = useState<string | undefined>();
  const [selectedProjects, setSelectedProjects] = useState<Maybe<string[]>>([]);

  const statusDetails = useMemo(() => enumDetails?.changeRequestStatus ?? [], [enumDetails]);
  const changeTypes = useMemo(() => enumDetails?.changeType.sortByKey("sortOrder") ?? [], [enumDetails]);
  const businessFunctionTypes = useMemo(() => enumDetails?.businessFunctionType ?? [], [enumDetails]);
  const changeRequestSources = useMemo(
    () => filterChangeRequestSources(enumDetails?.changeRequestSource ?? []),
    [enumDetails],
  );

  const filters = useMemo(() => ChangeLogViewFilterConfig[view].filters, [view]);

  const { data: projectsFilterData } = useChangeLogProjectFilterMetadataQuery({
    variables: { filter: { search: projectSearch }, page: { offset: 0, limit: 100 } },
    skip: !filters.includes(ChangeLogFilter.Project),
  });

  const { data: currentSelectedProjects } = useChangeLogProjectFilterMetadataQuery({
    variables: { filter: { id: selectedProjects }, page: { offset: 0, limit: 100 } },
    skip: !filters.includes(ChangeLogFilter.Project),
  });

  const projectsForFilter = useMemo(() => {
    const selected = currentSelectedProjects?.projectsPage.entities ?? [];
    const searched = projectsFilterData?.projectsPage.entities ?? [];
    return [...selected, ...searched].uniqueBy((p) => p.id);
  }, [currentSelectedProjects, projectsFilterData]);

  const filterDefs = useMemo(
    () =>
      cleanFilters({
        myRequests: maybeFilter(
          filters,
          ChangeLogFilter.MyRequests,
          checkboxFilter({ label: "My Requests", defaultValue: true }),
        ),
        status: maybeFilter(
          filters,
          ChangeLogFilter.Status,
          multiFilter({
            label: "Status",
            options: statusDetails,
            getOptionLabel: ({ name }) => name,
            getOptionValue: ({ code }) => code,
          }),
        ),
        source: maybeFilter(
          filters,
          ChangeLogFilter.Source,
          multiFilter({
            label: "Source",
            options: changeRequestSources,
            getOptionLabel: ({ name }) => name,
            getOptionValue: ({ code }) => code,
          }),
        ),
        changeTypes: maybeFilter(
          filters,
          ChangeLogFilter.ChangeType,
          multiFilter({
            label: "Type of Change",
            options: changeTypes,
            getOptionLabel: ({ name }) => name,
            getOptionValue: ({ code }) => code,
          }),
        ),
        businessFunction: maybeFilter(
          filters,
          ChangeLogFilter.Department,
          multiFilter({
            label: "Department",
            options: businessFunctionTypes,
            getOptionLabel: ({ name }) => name,
            getOptionValue: ({ code }) => code,
          }),
        ),
        project: maybeFilter(
          filters,
          ChangeLogFilter.Project,
          multiFilter({
            label: "Project",
            options: projectsForFilter,
            onSearch: (input) => setProjectSearch(input),
            getOptionLabel: ({ name }) => name,
            getOptionValue: ({ id }) => id,
            sizeToContent: true,
          }),
        ),
        group: maybeFilter(
          filters,
          ChangeLogFilter.Group,
          multiFilter({
            label: "Group",
            options: changeRequestGroups,
            getOptionLabel: ({ name }) => name,
            getOptionValue: ({ id }) => id,
            sizeToContent: true,
          }),
        ),
        market: maybeFilter(
          filters,
          ChangeLogFilter.Market,
          multiFilter({
            label: "Market",
            options: markets,
            getOptionLabel: ({ name }) => name,
            getOptionValue: ({ id }) => id,
          }),
        ),
        development: maybeFilter(
          filters,
          ChangeLogFilter.Development,
          multiFilter({
            label: "Development",
            options: developments.sortByKey("name"),
            getOptionLabel: ({ name }) => name,
            getOptionValue: ({ id }) => id,
          }),
        ),
      }),
    [
      filters,
      statusDetails,
      changeRequestSources,
      changeTypes,
      businessFunctionTypes,
      projectsForFilter,
      changeRequestGroups,
      markets,
      developments,
    ],
  );

  const layoutState = useGridTableLayoutState<ChangeLogTableFilter>({
    persistedFilter: { filterDefs, storageKey: `change-log-filter-${view}` },
    search: "server",
  });

  useEffect(() => {
    if (filters.includes(ChangeLogFilter.Project)) {
      setSelectedProjects(layoutState.filter.project);
    }
  }, [layoutState.filter.project, filters]);

  const actions: HeaderAction[] = useMemo(() => {
    const createAction: HeaderAction = {
      label: "Create",
      onClick: () =>
        openInDrawer({
          content: <CreateChangeRequestDrawer projectId={projectId} developmentId={developmentId} />,
          width: SuperDrawerWidth.Normal,
        }),
    };
    if (view !== ChangeLogView.Global) {
      return [createAction];
    }
    return [
      {
        kind: "menu",
        placement: "right",
        trigger: { icon: "cog" },
        items: [
          { label: "Change Types", onClick: createChangeTypesUrl() },
          { label: "Functions", onClick: createBusinessFunctionsUrl() },
        ],
      },
      createAction,
    ];
  }, [developmentId, openInDrawer, projectId, view]);

  return (
    <PageHeaderLayout pageHeader={{ title: "Change Log", actions }}>
      <ChangeLogTable
        view={view}
        layoutState={layoutState}
        changeTypes={changeTypes}
        projectId={projectId}
        developmentId={developmentId}
      />
    </PageHeaderLayout>
  );
}

type DetailsUrlParams = { changeRequestId: string; projectId?: string; developmentId?: string };

type ChangeLogViewConfig = {
  filters: ChangeLogFilter[];
  baseUrl: (params: Omit<DetailsUrlParams, "changeRequestId">) => string;
  detailsUrl: (params: DetailsUrlParams) => string;
};

export const ChangeLogViewFilterConfig: Record<ChangeLogView, ChangeLogViewConfig> = {
  [ChangeLogView.Global]: {
    filters: [
      ChangeLogFilter.MyRequests,
      ChangeLogFilter.Status,
      ChangeLogFilter.ChangeType,
      ChangeLogFilter.Source,
      ChangeLogFilter.Department,
      ChangeLogFilter.Market,
      ChangeLogFilter.Development,
      ChangeLogFilter.Project,
      ChangeLogFilter.Group,
    ],
    baseUrl: () => createGlobalChangeLogUrl(),
    detailsUrl: ({ changeRequestId }) => createGlobalChangeLogDetailsUrl(changeRequestId),
  },
  [ChangeLogView.Development]: {
    filters: [
      ChangeLogFilter.MyRequests,
      ChangeLogFilter.Status,
      ChangeLogFilter.ChangeType,
      ChangeLogFilter.Source,
      ChangeLogFilter.Department,
      ChangeLogFilter.Project,
      ChangeLogFilter.Group,
    ],
    baseUrl: ({ developmentId }) => createDevelopmentChangeLogUrl(developmentId!),
    detailsUrl: ({ changeRequestId, developmentId }) =>
      createDevelopmentChangeLogDetailsUrl(developmentId!, changeRequestId),
  },
  [ChangeLogView.Project]: {
    filters: [
      ChangeLogFilter.MyRequests,
      ChangeLogFilter.Status,
      ChangeLogFilter.ChangeType,
      ChangeLogFilter.Source,
      ChangeLogFilter.Department,
      ChangeLogFilter.Group,
    ],
    baseUrl: ({ projectId }) => createProjectChangeLogUrl(projectId!),
    detailsUrl: ({ changeRequestId, projectId }) => createProjectChangeLogDetailsUrl(projectId!, changeRequestId),
  },
};

const maybeFilter = <T,>(
  filters: ChangeLogFilter[],
  filterType: ChangeLogFilter,
  filter: (key: string) => FilterType<T>,
) => (filters.includes(filterType) ? filter : undefined);

const cleanFilters = (filters: Partial<Record<keyof ChangeLogTableFilter, Maybe<(key: string) => FilterType<any>>>>) =>
  Object.entries(filters)
    .filter(([_, value]) => value !== undefined)
    .reduce(
      (obj, [key, value]) => {
        obj[key as keyof ChangeLogTableFilter] = value!;
        return obj;
      },
      {} as Record<keyof ChangeLogTableFilter, (key: string) => FilterType<any>>,
    );
